import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
  Textarea,
  IconButton,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import CustomFieldDisplay from './CustomFieldDisplay';
import CustomFieldEditor from './CustomFieldEditor';
import { getCustomFieldValueType } from '../../../utils/tableUtils';

/**
 * Modal for viewing and editing full custom field content
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.fieldName - Name of the field
 * @param {*} props.fieldValue - Value to display
 * @param {Object} props.fieldDef - Field definition with output_schema
 * @param {Function} props.onSave - Save handler for editing
 */
const CustomFieldViewModal = ({ isOpen, onClose, fieldName, fieldValue, fieldDef, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [richEditValue, setRichEditValue] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [originalValue, setOriginalValue] = useState(null);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  
  const getFieldType = () => {
    if (fieldDef && fieldDef.output_schema) {
      const schemaType = fieldDef.output_schema.type;
      if (schemaType === 'number' || schemaType === 'integer') return 'number';
      if (schemaType === 'string') return 'string';
      if (schemaType === 'boolean') return 'boolean';
      if (schemaType === 'array') {
        if (fieldDef.output_schema.items && fieldDef.output_schema.items.type === 'object') {
          return 'array-of-objects';
        }
        return 'array';
      }
      if (schemaType === 'object') return 'object';
    }
    return getCustomFieldValueType(fieldValue);
  };
  
  const valueType = getFieldType();
  const isComplexType = ['array', 'array-of-objects', 'object'].includes(valueType);
  const isNumberType = valueType === 'number';
  
  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setValidationError('');
      setHasUserEdited(false);
      setOriginalValue(fieldValue);
      const currentValueType = getFieldType();
      const currentIsComplexType = ['array', 'array-of-objects', 'object'].includes(currentValueType);
      if (currentIsComplexType) {
        setRichEditValue(fieldValue);
      } else {
        setEditValue(formatValueForEdit(fieldValue));
      }
    }
  }, [isOpen, fieldValue, fieldDef]);
  
  useEffect(() => {
    if (!isEditing) {
      const currentValueType = getFieldType();
      const currentIsComplexType = ['array', 'array-of-objects', 'object'].includes(currentValueType);
      if (currentIsComplexType) {
        setRichEditValue(fieldValue);
      } else {
        setEditValue(formatValueForEdit(fieldValue));
      }
      setOriginalValue(fieldValue);
      setHasUserEdited(false);
    }
  }, [fieldValue, isEditing, fieldDef]);

  const formatValueForEdit = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object' || Array.isArray(value)) {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    }
    return String(value);
  };
  
  const handleSave = () => {
    if (!onSave) return;
    
    if (isComplexType) {
      onSave(richEditValue);
      setIsEditing(false);
      setHasUserEdited(false);
    } else {
      if (!hasUserEdited) {
        onSave(originalValue);
        setIsEditing(false);
        setValidationError('');
        return;
      }
      
      if (isNumberType) {
        const trimmed = editValue.trim();
        if (trimmed === '') {
          onSave(null);
          setIsEditing(false);
          setValidationError('');
          setHasUserEdited(false);
          return;
        }
        const numberValue = Number(trimmed);
        if (isNaN(numberValue)) {
          setValidationError('Please enter a valid number');
          return;
        }
        onSave(numberValue);
        setIsEditing(false);
        setValidationError('');
        setHasUserEdited(false);
      } else {
        onSave(editValue);
        setIsEditing(false);
        setValidationError('');
        setHasUserEdited(false);
      }
    }
  };
  
  const handleRichEditChange = (newValue) => {
    setRichEditValue(newValue);
    setHasUserEdited(true);
  };
  
  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };
  
  const handleTextClick = () => {
    if (!isEditing) setIsEditing(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const trimmedValue = editValue.trim();
      if (isNumberType && trimmedValue !== '') {
        const numberValue = Number(trimmedValue);
        if (isNaN(numberValue)) {
          e.preventDefault();
          setValidationError('Please enter a valid number');
          return;
        }
      }
      if (trimmedValue || !isNumberType) {
        e.preventDefault();
        handleSave();
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.85)" />
      <ModalContent
        bg="#111111"
        borderColor="rgba(255,255,255,0.10)"
        borderWidth="1px"
        borderRadius="10px"
        maxW="800px"
      >
        <ModalHeader
          color="#fafafa"
          fontSize="md"
          fontWeight="600"
          letterSpacing="-0.01em"
          borderBottom="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          pr={24}
          position="relative"
          display="flex"
          alignItems="center"
          minH="60px"
        >
          {fieldName}
          <Box
            position="absolute"
            right={4}
            top="50%"
            transform="translateY(-50%)"
            display="flex"
            alignItems="center"
            gap={2}
          >
            {isEditing && (
              <IconButton
                icon={<CheckIcon />}
                onClick={handleSave}
                size="sm"
                aria-label="Save changes"
                bg="#fafafa"
                color="#0a0a0a"
                borderRadius="full"
                _hover={{ opacity: 0.85 }}
              />
            )}
            <ModalCloseButton
              position="static"
              color="rgba(255,255,255,0.4)"
              _hover={{ color: 'rgba(255,255,255,0.75)', bg: 'rgba(255,255,255,0.06)' }}
              borderRadius="full"
            />
          </Box>
        </ModalHeader>
        <ModalBody py={6}>
          {!onSave && (
            <Box
              mb={4}
              px={3}
              py={2}
              borderRadius="8px"
              bg="rgba(234,179,8,0.08)"
              border="1px solid rgba(234,179,8,0.2)"
            >
              <Text fontSize="xs" color="rgba(234,179,8,0.85)">
                This campaign has been exported and is read-only.
              </Text>
            </Box>
          )}
          {isEditing ? (
            isComplexType ? (
              <Box
                p={4}
                bg="#0a0a0a"
                borderRadius="8px"
                maxH="500px"
                overflowY="auto"
                border="1px solid rgba(255,255,255,0.15)"
              >
                <CustomFieldEditor
                  value={richEditValue}
                  onChange={handleRichEditChange}
                  size="md"
                />
              </Box>
            ) : (
              <FormControl isInvalid={!!validationError}>
                <Textarea
                  value={editValue}
                  onChange={(e) => {
                    setEditValue(e.target.value);
                    setValidationError('');
                    setHasUserEdited(true);
                  }}
                  onKeyDown={handleKeyDown}
                  bg="#0a0a0a"
                  color="#fafafa"
                  borderColor={validationError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)'}
                  borderWidth="1px"
                  fontSize="sm"
                  minH="300px"
                  maxH="500px"
                  borderRadius="8px"
                  autoFocus
                  _hover={{ borderColor: 'rgba(255,255,255,0.25)' }}
                  _focus={{
                    borderColor: validationError
                      ? 'rgba(239,68,68,0.6)'
                      : 'rgba(255,255,255,0.3)',
                    boxShadow: 'none',
                  }}
                  _placeholder={{ color: 'rgba(255,255,255,0.2)' }}
                />
                {validationError && (
                  <FormErrorMessage mt={2} color="rgba(239,68,68,0.8)" fontSize="sm">
                    {validationError}
                  </FormErrorMessage>
                )}
              </FormControl>
            )
          ) : (
            <Box
              p={4}
              bg="#0a0a0a"
              borderRadius="8px"
              maxH="500px"
              overflowY="auto"
              cursor={onSave ? 'text' : 'default'}
              onClick={onSave ? handleTextClick : undefined}
              border="1px solid rgba(255,255,255,0.06)"
              _hover={onSave ? { borderColor: 'rgba(255,255,255,0.12)', bg: 'rgba(255,255,255,0.02)' } : {}}
              transition="all 0.15s"
            >
              <CustomFieldDisplay value={fieldValue} size="md" />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CustomFieldViewModal;
